import React, { useCallback, useState } from 'react';

import { MicroBlocked } from '@yandex-360/iconostasis/dist/icons/MicroBlocked';
import { MicroOff } from '@yandex-360/iconostasis/dist/icons/MicroOff';
import { MicroOn } from '@yandex-360/iconostasis/dist/icons/MicroOn';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { ModerationPopup } from '../../../../shared/components';
import { ModalKickConfirm } from '../../../../shared/components/modal-kick-confirm';
import { ModalStopRecordingConfirm } from '../../../../shared/components/modal-stop-recording-confirm';
import useModerationParticipantAudio from '../../../../shared/features/moderation/hooks/useModerationParticipantAudio';
import { i18n } from '../../../../shared/helpers/i18n';
import { GRID_VIEW } from '../../constants';
import { selectIsPermissionsExtendedEnabled } from '../../selectors';
import { ParticipantID } from '../../types';
import ModerationControlButton from '../moderation-control-button';
import css from './index.css';

interface Props {
    isAudioOn: boolean;
    displayName?: string;
    userId: string;
    activeGridView: GRID_VIEW;
    isFromOverlay: boolean;
    shouldShowMoreButtonPopup: boolean;
    shouldShowModerationControls: boolean;
    shouldShowModerationMicrophone: boolean;
    shouldShowModerationPopupButton: boolean;
    toggleMoreButtonPopup(): void;
    className?: string;
    isMobile?: boolean;
    isRecordingActive?: boolean;
}

const MAIN_OFFSET = 16;

export const ParticipantName: React.FC<Props> = (props) => {
    const {
        isAudioOn,
        displayName,
        userId,
        isFromOverlay,
        className,
        shouldShowMoreButtonPopup,
        shouldShowModerationControls,
        shouldShowModerationMicrophone,
        shouldShowModerationPopupButton,
        toggleMoreButtonPopup,
        isMobile,
        isRecordingActive,
    } = props;

    const isPermissionsExtendedEnabled = useSelector(selectIsPermissionsExtendedEnabled);
    const { muteParticipant, disableMicrophoneButton, isMutedTrack, hasMicrophonePermission } =
        useModerationParticipantAudio(userId);
    const nameWrapperRef = React.useRef<HTMLDivElement>(null);

    const participantName = displayName?.trim() || i18n('common', 'guest');

    const [participantIdToKick, setParticipantIdToKick] = React.useState<string | undefined>(undefined);
    const handleShowModalKickConfirm = React.useCallback((participantId: string) => {
        setParticipantIdToKick(participantId);
    }, []);
    const handleCloseModalKickConfirm = React.useCallback(() => setParticipantIdToKick(''), []);

    const [participantIdToStopRecording, setParticipantIdToStopRecording] = useState<ParticipantID | 'all' | null>(
        null
    );
    const handleShowModalStopRecording = useCallback((participantId: ParticipantID | 'all') => {
        setParticipantIdToStopRecording(participantId);
    }, []);
    const handleCloseModalStopRecordingConfirm = useCallback(() => setParticipantIdToStopRecording(null), []);

    if (isFromOverlay || (!displayName && isAudioOn)) {
        return null;
    }

    if (displayName === undefined) {
        return <div className={css.pulse}></div>;
    }

    const MicrophoneIcon = (() => {
        if (isPermissionsExtendedEnabled && !hasMicrophonePermission) {
            return MicroBlocked;
        }
        return isMutedTrack ? MicroOff : MicroOn;
    })();
    const microphoneIcon = <MicrophoneIcon variant="solid" size="sm" />;

    return (
        <div className={cn(css.NameWrapper, className)} ref={nameWrapperRef}>
            {shouldShowModerationControls && shouldShowMoreButtonPopup && (
                <ModerationPopup
                    offset={MAIN_OFFSET}
                    participantId={userId}
                    triggerRef={nameWrapperRef}
                    onClose={toggleMoreButtonPopup}
                    hideOnResize
                    onKickConfirm={handleShowModalKickConfirm}
                    onStopRecordingConfirm={handleShowModalStopRecording}
                    isRecordingActive={isRecordingActive}
                    isMobile={isMobile}
                />
            )}
            {participantIdToKick ? (
                <ModalKickConfirm onClose={handleCloseModalKickConfirm} participantId={participantIdToKick} />
            ) : null}
            <div
                className={css.Name}
                onClick={isMobile && shouldShowModerationPopupButton ? toggleMoreButtonPopup : undefined}
            >
                <span className={css.TextName} title={participantName}>
                    {participantName}
                </span>
                {shouldShowModerationControls ? (
                    <>
                        {shouldShowModerationMicrophone && (
                            <ModerationControlButton
                                testId="mute-audio"
                                className={css.ModerationControlButton}
                                iconClassName={css.ModerationButtonIcon}
                                icon={microphoneIcon}
                                disabled={disableMicrophoneButton}
                                onClick={muteParticipant}
                                title={isAudioOn ? i18n('toolbar', 'turn-off-mic') : i18n('toolbar', 'turn-on-mic')}
                            />
                        )}

                        {!isMobile && shouldShowModerationPopupButton && (
                            <ModerationControlButton
                                testId="show-moderation-popup"
                                className={cn(css.ModerationControlButton, css.ParticipantMoreButton)}
                                iconClassName={css.ModerationButtonIcon}
                                iconName="more"
                                onClick={toggleMoreButtonPopup}
                                title={i18n('toolbar', 'more-popup-alt-title')}
                            />
                        )}
                    </>
                ) : !isAudioOn ? (
                    <MicroOff testId="micro-off-icon" variant="solid" size="sm" className={css.MicroOffIcon} />
                ) : null}
            </div>
            {participantIdToStopRecording ? (
                <ModalStopRecordingConfirm
                    participantId={participantIdToStopRecording}
                    onClose={handleCloseModalStopRecordingConfirm}
                />
            ) : null}
        </div>
    );
};
